import {useEffect, useMemo, useRef, useState} from 'react';
import {trackEvent} from '../../utils/analytics';
import {showError} from '../../utils/notify';
import {skazkiAudio, useSkazkiAudio} from '../../utils/skazkiAudio';
import {formatDuration, SKAZKI_GENERATION_STATUSES} from '../../config/skazkiConstants';
import {getAlignment} from '../../api/skazki';
import {
    activeSentenceIndex,
    activeWordIndex,
    buildSentences,
    lastStartedIndex,
} from '../../utils/karaoke';

// Ночной плеер — единственный экран сказки: он же прогресс генерации (ассеты
// проявляются по мере готовности), он же превью с кнопкой «Открыть сказку
// целиком», он же обычный плеер готовой сказки. Текст подсвечивается по словам
// из alignment.json; без него — по сценам из playback.

// Детерминированное звёздное небо (позиции в %, размер в px, задержка в s).
const STARS = [
    {top: '14%', left: '7%', size: 3, delay: 0},
    {top: '9%', left: '22%', size: 2, delay: 1.2},
    {top: '24%', left: '15%', size: 2, delay: 2.1},
    {top: '7%', left: '38%', size: 2, delay: 0.6},
    {top: '19%', left: '47%', size: 3, delay: 1.8},
    {top: '11%', left: '61%', size: 2, delay: 2.6},
    {top: '27%', left: '69%', size: 2, delay: 0.9},
    {top: '8%', left: '83%', size: 3, delay: 1.5},
    {top: '22%', left: '91%', size: 2, delay: 2.9},
    {top: '41%', left: '11%', size: 2, delay: 0.4},
    {top: '38%', left: '31%', size: 2, delay: 2.3},
    {top: '46%', left: '55%', size: 3, delay: 1.1},
    {top: '36%', left: '80%', size: 2, delay: 0.2},
    {top: '52%', left: '24%', size: 2, delay: 1.9},
    {top: '58%', left: '72%', size: 2, delay: 2.7},
    {top: '49%', left: '90%', size: 2, delay: 1.4},
];

const ERROR_COPY = {
    network: {title: 'Подождите, восстанавливаем связь…', body: 'Как только вернётся — продолжим с того же места.'},
    failed: {title: 'Сегодня не получилось', body: 'Попробуем ещё раз?'},
    load: {title: 'Не удалось открыть сказку', body: 'Попробуйте снова.'},
};

export default function SkazkiNightPlayer({
                                              tale,
                                              voiceName,
                                              error = null,
                                              unlocking = false,
                                              onUnlock,
                                              onRetry,
                                              onBack,
                                              onCreateAnother,
                                          }) {
    const audioState = useSkazkiAudio();
    const [alignment, setAlignment] = useState(null);
    const [statusIdx, setStatusIdx] = useState(0);
    const [clock, setClock] = useState(0);
    const [shareCopied, setShareCopied] = useState(false);
    const [listenedReported, setListenedReported] = useState(false);

    const failed = !!error || tale.status === 'failed';
    const generating = !failed && tale.status === 'processing';
    const audioReady = !!tale.audio_url;
    const coverReady = !!tale.cover_url;
    const titleReady = !!tale.has_title;
    const textReady = !!(tale.chapters?.length || tale.text);
    const locked = !tale.paid;
    const showUnlock = locked && !!onUnlock && !failed;

    // Держим сингл-плеер в актуальном состоянии: после /unlock у той же сказки
    // появляется новый audio_url — loadTale сам перезаряжает src. Если озвучка
    // появилась, пока плеер открыт (пользователь дождался генерацию или анлок),
    // запускаем её сразу — жест уже был (создание/анлок), autoplay разрешён.
    // Сказку, открытую уже готовой (по ссылке/из списка), не автоплеим: там
    // жеста не было и браузер всё равно заблокирует.
    const prevHadAudioRef = useRef(!!tale.audio_url);
    useEffect(() => {
        const had = prevHadAudioRef.current;
        prevHadAudioRef.current = !!tale.audio_url;
        if (!tale.audio_url) return;
        skazkiAudio.loadTale(tale);
        if (!had) {
            trackEvent('skazki_autoplay');
            skazkiAudio.play();
        }
    }, [tale]);

    // Пословная разметка (alignment.json) подгружается, как только готово
    // аудио (alignment_url в снапшоте — признак готовности). Сам файл берём
    // через API-прокси: у S3-бакета нет CORS.
    useEffect(() => {
        setAlignment(null);
        if (!tale.alignment_url || !tale.id) return undefined;
        let cancelled = false;
        getAlignment(tale.id, {fallbackUrl: tale.alignment_url})
            .then(doc => {
                if (!cancelled) setAlignment(doc);
            })
            .catch(() => { /* остаёмся на посценном фолбэке */
            });
        return () => {
            cancelled = true;
        };
    }, [tale.id, tale.alignment_url]);

    useEffect(() => {
        if (locked) trackEvent('see_paywall_skazki');
    }, [locked]);

    // Ротация статусов, пока сказка генерируется.
    useEffect(() => {
        if (!generating) return undefined;
        const t = setInterval(
            () => setStatusIdx(i => (i + 1) % SKAZKI_GENERATION_STATUSES.length),
            3200,
        );
        return () => clearInterval(t);
    }, [generating]);

    // Плавные (rAF) часы для подсветки слов — timeupdate слишком редкий.
    useEffect(() => {
        if (!audioState.isPlaying) return undefined;
        let raf;
        const tick = () => {
            setClock(skazkiAudio.getCurrentTime());
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [audioState.isPlaying]);
    const time = audioState.isPlaying ? clock : audioState.currentSec;

    const duration = tale.duration_sec || audioState.durationSec || 0;
    const progress = duration > 0 ? Math.min(1, time / duration) : 0;

    useEffect(() => {
        if (!duration || listenedReported) return;
        if (audioState.currentSec / duration >= 0.9) {
            trackEvent('skazki_listen_complete');
            setListenedReported(true);
        }
    }, [audioState.currentSec, duration, listenedReported]);

    const sentences = useMemo(
        () => (alignment?.words?.length ? buildSentences(alignment.words) : []),
        [alignment],
    );
    const sentenceIdx = sentences.length ? Math.max(0, activeSentenceIndex(sentences, time)) : -1;
    const sentence = sentenceIdx >= 0 ? sentences[sentenceIdx] : null;
    const wordIdx = sentence ? activeWordIndex(sentence.words, time) : -1;

    // Без alignment показываем текущую сцену (по таймингам playback), а до
    // готовности аудио — просто начало текста.
    const fallbackText = useMemo(() => {
        if (!tale.chapters?.length) return tale.text || null;
        const timed = tale.chapters.filter(c => c.start != null);
        if (audioReady && timed.length) {
            const i = Math.max(0, lastStartedIndex(timed, time, c => c.start));
            return timed[i]?.text || null;
        }
        return tale.chapters[0]?.text || null;
    }, [tale.chapters, tale.text, audioReady, time]);

    const toggle = () => {
        if (!audioReady) return;
        trackEvent(locked ? 'skazki_preview_play' : 'skazki_play');
        skazkiAudio.toggle();
    };

    const seekAt = (e) => {
        if (!audioReady || !duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        skazkiAudio.seekTo(frac * duration);
        setClock(frac * duration);
    };

    const handleShare = async () => {
        const url = `${window.location.origin}/skazki/s/${tale.id}`;
        trackEvent('skazki_share');
        if (navigator.share) {
            try {
                await navigator.share({title: tale.title, url});
                return;
            } catch (err) {
                if (err.name === 'AbortError') return;
            }
        }
        try {
            await navigator.clipboard.writeText(url);
            setShareCopied(true);
            setTimeout(() => setShareCopied(false), 2000);
        } catch {
            showError('Не удалось скопировать ссылку');
        }
    };

    const handleDownload = () => {
        if (!tale.audio_url) return;
        trackEvent('skazki_download');
        const a = document.createElement('a');
        a.href = tale.audio_url;
        a.download = `${tale.title}.mp3`;
        a.click();
    };

    const subtitleParts = [
        voiceName ? `Озвучка «${voiceName}»` : 'Озвучка',
        audioReady ? formatDuration(duration) : (generating ? 'записываем…' : '—'),
    ];
    if (locked && audioReady) subtitleParts.push('превью');

    return (
        <div className="max-w-[1160px] mx-auto md:px-4 md:my-8">
            <div
                className="sk-night md:rounded-[28px] overflow-hidden flex flex-col min-h-[calc(100dvh-140px)] md:min-h-[700px] md:shadow-[0_24px_80px_rgba(10,14,40,0.45)]">

                {/* Готовая обложка занимает всё небо (под вуалью) */}
                {coverReady && (
                    <div className="sk-night-cover">
                        <img src={tale.cover_url} alt={`Обложка сказки «${tale.title}»`}/>
                    </div>
                )}

                {/* Звёзды */}
                <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                    {STARS.map((s, i) => (
                        <span
                            key={i}
                            className="sk-night-star"
                            style={{
                                top: s.top,
                                left: s.left,
                                width: s.size,
                                height: s.size,
                                animationDelay: `${s.delay}s`,
                            }}
                        />
                    ))}
                </div>

                {/* Верхняя строка: назад + бейдж превью */}
                <div className="relative z-10 flex items-center justify-between px-4 pt-4 md:px-7 md:pt-6">
                    <button type="button" onClick={onBack} className="sk-night-back" aria-label="Назад">
                        <BackGlyph/>
                    </button>
                    {locked && (
                        <span
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#E9EFFF]/25 px-3 py-1 text-[11px] font-bold tracking-[0.14em] uppercase text-[#E9EFFF]/75">
                            <LockGlyph/> превью
                        </span>
                    )}
                </div>

                {/* Заголовок + обложка-«луна» (на десктопе луна справа, на
                    мобиле — под заголовком, в «небе») */}
                <div className="relative z-10 mt-5 md:mt-9 px-5">
                    <div className="text-center md:px-[200px]">
                        <div className="sk-night-eyebrow">
                            {locked ? 'Превью сказки' : 'Ваша сказка'}
                        </div>

                        {titleReady ? (
                            <h1 className="sk-night-reveal mt-2.5 font-bold tracking-[-0.015em] leading-[1.12] text-[26px] md:text-[42px] text-[#FDFBF5] [text-wrap:balance]">
                                {tale.title}
                            </h1>
                        ) : (
                            <div className="mt-4 flex flex-col items-center gap-2.5" aria-hidden="true">
                                <div className="sk-night-skel h-[24px] md:h-[36px] w-[230px] md:w-[420px]"/>
                                <div className="sk-night-skel h-[24px] md:h-[36px] w-[140px] md:w-[230px]"/>
                            </div>
                        )}

                        <div className="mt-2.5 text-[13px] md:text-[15px] text-[#E9EFFF]/70">
                            {subtitleParts.join(' · ')}
                        </div>

                        {/* Готовность ассетов — появляются один за другим */}
                        {generating && (
                            <div className="mt-4 flex justify-center gap-2 flex-wrap">
                                <AssetChip done={textReady} label="Текст"/>
                                <AssetChip done={coverReady} label="Обложка"/>
                                <AssetChip done={audioReady} label="Озвучка"/>
                            </div>
                        )}
                    </div>

                    {/* Луна-заглушка живёт в небе, пока обложка не нарисована;
                        готовая обложка уходит в фон, и луна гаснет. */}
                    {!coverReady && (
                        <div
                            className="flex justify-center mt-6 md:mt-0 md:absolute md:right-[6%] md:top-[-12px]">
                            <div
                                className={`sk-night-moon ${generating ? 'sk-night-moon--skeleton' : ''} w-[112px] h-[112px] md:w-[150px] md:h-[150px]`}/>
                        </div>
                    )}
                </div>

                <div className="flex-1 min-h-[28px]"/>

                {/* «Сейчас читаем» — прямо на небе/обложке, без подложки */}
                <div className="relative z-10 w-full max-w-[900px] mx-auto px-4 md:px-6 min-h-[116px] md:min-h-[140px] flex flex-col justify-end">
                    {failed ? (
                        <ErrorBody kind={error || 'failed'} onRetry={onRetry} onBack={onBack}/>
                    ) : (
                        <>
                            <div className="sk-night-read-eyebrow">
                                {sentence || fallbackText
                                    ? 'Сейчас читаем'
                                    : SKAZKI_GENERATION_STATUSES[statusIdx] || 'Сочиняем сказку…'}
                            </div>
                            <div className="mt-2.5 md:mt-3">
                                {sentence ? (
                                    <p className="sk-night-line sk-night-reveal">
                                        {sentence.words.map((w, i) => (
                                            <span key={w.index}>
                                                <span
                                                    className={`sk-night-word ${i === wordIdx ? 'sk-night-word--on' : ''}`}>
                                                    {w.w}
                                                </span>
                                                {i < sentence.words.length - 1 ? ' ' : ''}
                                            </span>
                                        ))}
                                    </p>
                                ) : fallbackText ? (
                                    <div className="sk-night-reveal">
                                        <p className="sk-night-line line-clamp-3">{fallbackText}</p>
                                        {!audioReady && (
                                            <p className="mt-2 text-[12px] text-[#E9EFFF]/60">
                                                Текст готов — записываем озвучку…
                                            </p>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-2.5 py-1" aria-hidden="true">
                                        <div className="sk-night-skel h-[18px] md:h-[22px] w-full"/>
                                        <div className="sk-night-skel h-[18px] md:h-[22px] w-[62%]"/>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Прогресс + управление */}
                <div className="relative z-10 w-full max-w-[900px] mx-auto px-4 md:px-6 mt-4 md:mt-5 pb-7 md:pb-9">
                    <div
                        className={`sk-night-bar ${audioReady ? '' : 'sk-night-bar--idle'}`}
                        onClick={seekAt}
                        role="slider"
                        aria-label="Позиция воспроизведения"
                        aria-valuemin={0}
                        aria-valuemax={Math.round(duration)}
                        aria-valuenow={Math.round(time)}
                        aria-disabled={!audioReady}
                    >
                        <div className="sk-night-bar-fill" style={{width: `${progress * 100}%`}}/>
                    </div>
                    <div
                        className="flex items-center justify-between text-[11px] text-[#E9EFFF]/50 mt-1.5 tabular-nums">
                        <span>{formatDuration(time)}</span>
                        <span>{audioReady ? formatDuration(duration) : '–:––'}</span>
                    </div>

                    <div className="mt-3 md:mt-4 flex items-center justify-center gap-4 md:gap-5 flex-wrap">
                        <button
                            type="button" className="sk-night-skip"
                            onClick={() => skazkiAudio.seekBy(-15)}
                            disabled={!audioReady}
                            aria-label="Назад на 15 секунд"
                        >−15
                        </button>
                        <button
                            type="button" className="sk-night-play"
                            onClick={toggle}
                            disabled={!audioReady}
                            aria-label={audioState.isPlaying ? 'Пауза' : 'Слушать'}
                        >
                            {audioState.isPlaying ? <PauseGlyph/> : <PlayGlyph/>}
                        </button>
                        <button
                            type="button" className="sk-night-skip"
                            onClick={() => skazkiAudio.seekBy(15)}
                            disabled={!audioReady}
                            aria-label="Вперёд на 15 секунд"
                        >+15
                        </button>

                        {showUnlock && (
                            <button
                                type="button"
                                className="sk-night-unlock w-full sm:w-auto justify-center"
                                onClick={onUnlock}
                                disabled={unlocking || tale.status !== 'ready'}
                            >
                                {unlocking
                                    ? <span
                                        className="inline-block w-4 h-4 border-2 border-[#E9EFFF]/40 border-t-[#E9EFFF] rounded-full animate-spin"/>
                                    : <LockGlyph gold/>}
                                {unlocking ? 'Открываем…' : 'Открыть сказку целиком'}
                            </button>
                        )}
                    </div>

                    {!locked && audioReady && (
                        <div className="mt-4 md:mt-5 flex items-center justify-center gap-5 flex-wrap">
                            <button type="button" className="sk-night-ghostlink" onClick={handleDownload}>
                                ↓ Скачать MP3
                            </button>
                            <button type="button" className="sk-night-ghostlink" onClick={handleShare}>
                                {shareCopied ? 'Ссылка скопирована' : 'Поделиться'}
                            </button>
                            {onCreateAnother && (
                                <button type="button" className="sk-night-ghostlink" onClick={onCreateAnother}>
                                    + Ещё сказку
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function AssetChip({done, label}) {
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold tracking-[0.04em] border ${
                done
                    ? 'border-[#E9EFFF]/40 text-[#FDFBF5]'
                    : 'border-[#E9EFFF]/15 text-[#E9EFFF]/45'
            }`}
        >
            {done
                ? <CheckGlyph/>
                : <span className="w-[6px] h-[6px] rounded-full bg-[#E9EFFF]/45 animate-pulse"/>}
            {label}
        </span>
    );
}

function ErrorBody({kind, onRetry, onBack}) {
    const copy = ERROR_COPY[kind] || ERROR_COPY.failed;
    return (
        <div>
            <p className="text-[16px] md:text-[18px] font-bold text-[#FDFBF5]">{copy.title}</p>
            <p className="text-[14px] text-[#E9EFFF]/70 mt-1">{copy.body}</p>
            <div className="flex gap-3 mt-4">
                {onRetry && (
                    <button
                        type="button" onClick={onRetry}
                        className="h-[42px] px-5 rounded-full bg-[#F7F1E3] text-[#221D2D] text-[14px] font-bold hover:opacity-90 transition"
                    >
                        Попробовать снова
                    </button>
                )}
                <button
                    type="button" onClick={onBack}
                    className="h-[42px] px-4 rounded-full text-[14px] font-semibold text-[#E9EFFF]/65 hover:text-[#FDFBF5] transition"
                >
                    Назад
                </button>
            </div>
        </div>
    );
}

function PlayGlyph() {
    return (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M8.2 5.6v12.8a.7.7 0 0 0 1.07.6l10.1-6.4a.7.7 0 0 0 0-1.2L9.27 5a.7.7 0 0 0-1.07.6z"/>
        </svg>
    );
}

function PauseGlyph() {
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <rect x="5.5" y="4.5" width="4.6" height="15" rx="1.6"/>
            <rect x="13.9" y="4.5" width="4.6" height="15" rx="1.6"/>
        </svg>
    );
}

function CheckGlyph() {
    return (
        <svg width="11" height="11" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M2.5 7.4l3 3 6-6.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                  strokeLinejoin="round"/>
        </svg>
    );
}

function LockGlyph({gold = false}) {
    return (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true"
             className={gold ? 'text-[#F2C078]' : undefined}>
            <rect x="2.5" y="6" width="9" height="6.4" rx="1.6" fill="currentColor"/>
            <path d="M4.6 6V4.4a2.4 2.4 0 0 1 4.8 0V6" stroke="currentColor" strokeWidth="1.4"/>
        </svg>
    );
}

function BackGlyph() {
    return (
        <svg width="17" height="17" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8"
             strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M11 4l-5 5 5 5"/>
        </svg>
    );
}
